import { createSlice } from '@reduxjs/toolkit'

import { RootState } from '../store'
import { AuthenticatedUser, AuthenticatedUserTokenDetails } from '../../models/User'
import {
  confirmForgotPasswordWithCode,
  confirmRegistration,
  confirmSignUpWithVerificationCode,
  createUserFromToken,
  crossAccountLogin,
  forgotPasswordWithCode,
  getTokenForAssociationCode,
  loginWithMFATokenV2,
  loginWithUsernamePassword,
  loginWithUsernamePasswordV2,
  parseUser,
  refreshToken,
  signUpWithUsernamePasswordV2,
} from './authActions'
import { ACCOUNT_ID_NAME, BI_TOKEN_NAME, MFA_COMPLIANCE_SETUP_PENDING } from '../../common/constants'
import { Cookies } from 'react-cookie'
import { readToken, removeToken, saveToken, Token } from '../../common/token'

interface LoginChallenge {
  name: string
  session: string
}

interface LoginRedirect {
  accountId: string | null
  url: string | null
}

interface AuthState {
  user: AuthenticatedUser | null
  tokenDetails: AuthenticatedUserTokenDetails | null
  onboardingEnabled: boolean
  onboardingCode: string | null
  errors: Array<any> | null
  message: string | null
  loginRedirect: LoginRedirect
  loginChallenge?: LoginChallenge
  forgotPasswordStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  crossAccountLoginLoading: boolean
}

const removeGhostTokenCookies = () => {
  const cookies = new Cookies()
  cookies.remove('ghostAccessToken_1', { path: '/', domain: '.zocks.io' })
  cookies.remove('ghostAccessToken_2', { path: '/', domain: '.zocks.io' })
}

const getInitialUser = () => {
  const cookies = new Cookies()
  const accessToken = cookies.get('ghostAccessToken_1') + cookies.get('ghostAccessToken_2')
  if (accessToken) {
    saveToken(new Token(accessToken))
    removeGhostTokenCookies()
  }
  const token = readToken()
  if (token) {
    if (!token.isAccessTokenExpired) {
      return createUserFromToken(token)
    } else {
      localStorage.removeItem(BI_TOKEN_NAME)
    }
  }
  return { user: null, tokenDetails: null }
}

const initialState: AuthState = {
  ...getInitialUser(),
  onboardingEnabled: true,
  onboardingCode: null,
  errors: null,
  message: null,
  loginRedirect: {
    accountId: null,
    url: null,
  },
  forgotPasswordStatus: 'idle',
  crossAccountLoginLoading: false,
}

const havePermissionsChanged = (prev: AuthenticatedUser | null, next: AuthenticatedUser): boolean => {
  if (!prev) {
    return true
  }

  const prevPermissions = prev.permissions ?? []
  const nextPermissions = next.permissions ?? []

  if (prevPermissions.length !== nextPermissions.length) {
    return true
  }

  const prevPermissionsSet = new Set(prevPermissions)

  return nextPermissions.some((p) => !prevPermissionsSet.has(p))
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state, { payload }) => {
      removeToken()
      window.localStorage.removeItem(BI_TOKEN_NAME)
      window.localStorage.removeItem(ACCOUNT_ID_NAME)
      window.localStorage.removeItem(MFA_COMPLIANCE_SETUP_PENDING)
      removeGhostTokenCookies()
      state.loginRedirect.url = !/logout$/.test(payload.pathname) ? `${payload.pathname ?? ''}${payload.search ?? ''}` : '/'
      state.loginRedirect.accountId = state.user?.accountId || null
      state.user = null
      state.tokenDetails = null
      state.loginChallenge = undefined
    },
    setLoginRedirectUrl: (state, { payload }) => {
      state.loginRedirect.url = payload
      state.loginRedirect.accountId = state.user?.accountId || null
    },
    clearAuth: (state) => {
      state.user = null
      state.tokenDetails = null
      state.onboardingCode = null
      state.errors = null
      state.message = null
      state.loginChallenge = undefined
      state.loginRedirect.accountId = null
    },
    clearAuthErrors: (state) => {
      state.errors = null
    },
    clearForgotPasswordStatus: (state) => {
      state.forgotPasswordStatus = 'idle'
    },
    tokenRefreshed: (state, { payload }) => {
      const { tokenDetails, user } = parseUser(payload, { isTokenRefresh: true })
      if (havePermissionsChanged(state.user, user)) {
        state.user = user
      }
      state.tokenDetails = tokenDetails
      state.onboardingCode = null
      state.loginChallenge = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginWithUsernamePassword.pending, (state) => {
      state.errors = null
    })
    builder.addCase(loginWithUsernamePassword.fulfilled, (state, { payload }) => {
      if ('session' in payload) {
        state.loginChallenge = {
          name: payload.name,
          session: payload.session,
        }
      } else {
        const { user, tokenDetails } = payload
        state.user = user
        state.tokenDetails = tokenDetails
        state.loginChallenge = undefined
      }
    })
    // @ts-ignore
    builder.addCase(loginWithUsernamePassword.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(loginWithUsernamePasswordV2.pending, (state) => {
      state.errors = null
    })
    builder.addCase(loginWithUsernamePasswordV2.fulfilled, (state, { payload }) => {
      if ('message' in payload) {
        state.message = payload.message
      } else {
        const { user, tokenDetails } = payload
        state.user = user
        state.tokenDetails = tokenDetails
      }
      state.onboardingCode = null
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(loginWithMFATokenV2.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(loginWithMFATokenV2.pending, (state) => {
      state.errors = null
    })
    builder.addCase(loginWithMFATokenV2.fulfilled, (state, { payload }) => {
      if ('message' in payload) {
        state.message = payload.message
      } else {
        const { user, tokenDetails } = payload
        state.user = user
        state.tokenDetails = tokenDetails
      }
      state.onboardingCode = null
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(loginWithUsernamePasswordV2.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(getTokenForAssociationCode.pending, (state) => {
      state.errors = null
      state.message = null
    })
    builder.addCase(getTokenForAssociationCode.fulfilled, (state, { payload }) => {
      const { user, tokenDetails, onboardingEnabled, onboardingCode, message } = payload
      state.user = user
      state.tokenDetails = tokenDetails
      state.onboardingEnabled = onboardingEnabled ?? false
      state.onboardingCode = onboardingCode || null
      state.message = message
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(getTokenForAssociationCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(signUpWithUsernamePasswordV2.pending, (state) => {
      state.errors = null
    })
    builder.addCase(signUpWithUsernamePasswordV2.fulfilled, (state) => {
      state.errors = null
      state.loginChallenge = { name: 'confirmationCode', session: '' }
    })
    // @ts-ignore
    builder.addCase(signUpWithUsernamePasswordV2.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(confirmSignUpWithVerificationCode.pending, (state) => {
      state.errors = null
      state.message = null
    })
    builder.addCase(confirmSignUpWithVerificationCode.fulfilled, (state, { payload }) => {
      const { user, tokenDetails, onboardingEnabled, onboardingCode, message } = payload
      state.user = user
      state.tokenDetails = tokenDetails
      state.onboardingEnabled = onboardingEnabled ?? false
      state.onboardingCode = onboardingCode || null
      state.message = message
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(confirmSignUpWithVerificationCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(confirmRegistration.pending, (state) => {
      state.errors = null
    })
    builder.addCase(confirmRegistration.fulfilled, (state, { payload }) => {
      const { user, tokenDetails } = payload
      state.user = user
      state.tokenDetails = tokenDetails
      state.loginChallenge = undefined
    })
    // @ts-ignore
    builder.addCase(confirmRegistration.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
    })
    builder.addCase(forgotPasswordWithCode.pending, (state) => {
      state.errors = null
      state.forgotPasswordStatus = 'loading'
    })
    // @ts-ignore
    builder.addCase(forgotPasswordWithCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.forgotPasswordStatus = 'failed'
    })
    builder.addCase(forgotPasswordWithCode.fulfilled, (state) => {
      state.forgotPasswordStatus = 'succeeded'
    })
    builder.addCase(confirmForgotPasswordWithCode.pending, (state) => {
      state.forgotPasswordStatus = 'loading'
      state.errors = null
    })
    // @ts-ignore
    builder.addCase(confirmForgotPasswordWithCode.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.forgotPasswordStatus = 'failed'
    })
    builder.addCase(confirmForgotPasswordWithCode.fulfilled, (state) => {
      state.forgotPasswordStatus = 'succeeded'
    })
    builder.addCase(refreshToken.fulfilled, (state, { payload }) => {
      if ('message' in payload) {
        state.message = payload.message
      } else {
        const { tokenDetails, user } = payload
        state.tokenDetails = tokenDetails

        if (havePermissionsChanged(state.user, user)) {
          state.user = user
        }
      }
      state.onboardingCode = null
      state.loginChallenge = undefined
    })
    builder.addCase(refreshToken.pending, (state) => {
      state.errors = null
    })
    // @ts-ignore
    builder.addCase(refreshToken.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      removeToken()
      state.user = null
      state.tokenDetails = null
      state.loginChallenge = undefined
    })
    builder.addCase(crossAccountLogin.fulfilled, (state, { payload }) => {
      if ('message' in payload) {
        state.message = payload.message
      } else {
        const { tokenDetails, user } = payload
        state.tokenDetails = tokenDetails
        if (!state.user) {
          state.user = user
        }
      }
      state.onboardingCode = null
      state.loginChallenge = undefined
      state.crossAccountLoginLoading = false
    })
    builder.addCase(crossAccountLogin.pending, (state) => {
      state.errors = null
      state.crossAccountLoginLoading = true
    })
    // @ts-ignore
    builder.addCase(crossAccountLogin.rejected, (state, { payload: { errors } }) => {
      state.errors = errors
      state.crossAccountLoginLoading = false
    })
  },
})

export const { logout, clearAuthErrors, tokenRefreshed } = authSlice.actions

export const selectAuthenticatedUser = (state: RootState) => state.auth.user

export const selectSafeAuthenticatedUser = (state: RootState) => {
  if (!state.auth.user) {
    throw new Error('No authenticated user')
  }
  return state.auth.user
}
export const selectAccountId = (state: RootState) => {
  if (!state.auth.user) {
    throw new Error('No account id available without an authenticated user.')
  }
  return state.auth.user.accountId
}
export const selectAuthErrors = (state: RootState) => state.auth.errors
export const selectAuthenticatedUserToken = (state: RootState) => state.auth.tokenDetails?.token ?? null
export const selectAuthenticatedUserTokenExpiration = (state: RootState) => state.auth.tokenDetails?.tokenExpiration ?? null
export const selectCrossAccountLoginLoading = (state: RootState) => state.auth.crossAccountLoginLoading
export const selectIsSystemGhost = (state: RootState) => state.auth.user?.isSystemGhost ?? false

export default authSlice.reducer
